import some from "lodash/some";
import { lift, toggleMark as toggleMarkRun, wrapIn, } from "prosemirror-commands";
import { CellSelection } from "prosemirror-tables";
import { hasParentNode, hasParentNodeOfType } from "prosemirror-utils";
import { getParentNodeClosestToPos } from "../core/helpers";
import { toggleList } from "./list-commands";
/**
 * Creates a new EditorCommand that toggles the given MarkType.
 * Can be used by bold, italic, etc
 */
export function toggleMark(type, attrs) {
    return {
        enable: () => true,
        active: (state) => isMarkActive(state, type, attrs),
        run: toggleMarkRun(type, attrs),
    };
}
/**
 * Creates a new EditorCommand that applies the given MarkType and attributes.
 * Unline `createToggleMarkItem`, it first removes all marks of the same type from given selection
 * and then apply the new mark with updated attributes.
 */
export function replaceMark(type, attrs) {
    return {
        enable: () => true,
        active: (state) => isMarkActive(state, type, attrs),
        run: (state, dispatch) => {
            const { ranges } = state.selection;
            const transaction = state.tr;
            ranges.forEach(({ $from, $to }) => {
                transaction.removeMark($from.pos, $to.pos, type);
                transaction.addMark($from.pos, $to.pos, type.create(attrs));
            });
            dispatch(transaction);
        },
    };
}
/**
 * Creates a new EditorCommand that removes the given MarkType.
 */
export function removeMark(type, attrs) {
    return {
        enable: () => true,
        active: (state) => isMarkActive(state, type, attrs),
        run: (state, dispatch) => {
            const { from, to } = state.selection;
            dispatch(state.tr.removeMark(from, to, type));
        },
    };
}
/**
 * Creates a new EditorCommand that changes the immediate parent block node for the given type and attributes.
 * Can be used by paragraph, headings, etc
 */
export function changeParentNode(nodeType, attrs) {
    return {
        enable: ({ schema, selection }) => !hasParentNodeOfType(schema.nodes.check_list)(selection),
        active: (state) => isNodeActive(state, nodeType, attrs),
        run: (state, dispatch) => {
            /*
             * Initially this function called setBlockType from
             * 'prosemirror-commands'. However, this function uses the pos of the
             * current selection instead of the ranges which breaks on CellSelections.
             * Instead we iterate over each range in the current selection and call
             * setBlockType on it. If there's a better way to do this please change
             * this!
             */
            const { ranges } = state.selection;
            const transaction = state.tr;
            ranges.forEach(({ $from, $to }) => {
                transaction.setBlockType($from.pos, $to.pos, nodeType, attrs);
            });
            dispatch(transaction);
        },
    };
}
/**
 * Creates a new EditorCommand that wraps selected text with the given node type.
 * Can be used by paragraph, headings, etc
 */
export function wrapSelectedText(nodeType, attrs) {
    return {
        enable: ({ schema, selection }) => !hasParentNodeOfType(schema.nodes.check_list)(selection),
        active: (state) => isNodeActive(state, nodeType, attrs),
        run: (state, dispatch) => {
            const { from, to, ranges } = state.selection;
            const transaction = state.tr;
            if (state.selection instanceof CellSelection) {
                ranges.forEach(({ $from, $to }) => {
                    transaction.setBlockType($from.pos, $to.pos, nodeType, attrs);
                });
            }
            else {
                state.doc.nodesBetween(from, to, (node, position) => {
                    if (node.isText) {
                        const selectionStart = Math.max(0, from - position);
                        const selectionEnd = Math.max(0, to - position);
                        const selectedText = node.textContent.substring(selectionStart, selectionEnd);
                        const selectedTextNode = state.schema.text(selectedText, node.marks);
                        const wrapperNode = nodeType.create(attrs, selectedTextNode);
                        const isStartOfNode = position === from;
                        const startPosition = isStartOfNode
                            ? position - 1
                            : Math.max(position, from);
                        const endPosition = Math.min(position + node.nodeSize, to);
                        transaction.replaceWith(startPosition, endPosition, wrapperNode);
                    }
                });
            }
            dispatch(transaction);
        },
    };
}
/**
 * Creates a new EditorCommand that applies to the closest parent block node the given attributes.
 * Can be used by paragraph, paragraph_small and headings.
 */
export function updateNodeAttributes(attrs) {
    return {
        enable: (state) => {
            return !(state.selection instanceof CellSelection);
        },
        active: (state) => {
            const parent = getParentNodeClosestToPos(state);
            return (parent === null || parent === void 0 ? void 0 : parent.node.attrs.textAlign) === attrs.textAlign;
        },
        run: (state, dispatch) => {
            const parent = getParentNodeClosestToPos(state);
            if (!parent)
                return;
            const transaction = state.tr.setNodeMarkup(parent.pos, undefined, {
                ...parent.node.attrs,
                ...attrs,
            });
            dispatch(transaction);
        },
    };
}
/**
 * Creates a new EditorCommand that wraps/unwraps the selection in a given node type.
 * Can be used by blockquote, etc
 */
export function toggleBlockNode(nodeType) {
    return {
        enable: (state) => {
            return !(state.selection instanceof CellSelection);
        },
        active: (state) => isNodeActive(state, nodeType),
        run: (state, dispatch) => {
            if (isNodeActive(state, nodeType)) {
                return lift(state, dispatch);
            }
            else {
                return wrapIn(nodeType)(state, dispatch);
            }
        },
    };
}
/**
 * Creates a new EditorCommand that wraps/unwraps the selection in a given list itemType.
 * To be used by list and list item types.
 */
export function toggleListNode(nodeType, itemType) {
    return {
        enable: (state) => {
            return !(state.selection instanceof CellSelection);
        },
        active: (state) => isNodeActive(state, nodeType),
        run: toggleList(nodeType, itemType),
    };
}
/**
 * Checks if the node is active.
 * The node is considered active if current selection contains a parent node of given type and attributes.
 */
export function isNodeActive(state, type, attrs) {
    const predicate = hasParentNode((node) => node.type === type && some([node.attrs], attrs));
    return predicate(state.selection);
}
/**
 * Checks if the the active node is one of the list nodes.
 */
export function listNodeActive(state) {
    const listTypes = ["bullet_list", "ordered_list"];
    const predicate = hasParentNode((node) => listTypes.includes(node.type.name));
    return predicate(state.selection);
}
/**
 * Checks if the mark is active.
 * The mark is considered active if current selection or stored marks contain the mark of given type and attributes.
 */
export function isMarkActive(state, type, attrs) {
    const { from, $from, to, empty } = state.selection;
    if (empty) {
        const mark = type.isInSet(state.storedMarks || $from.marks());
        return mark ? some([mark.attrs], attrs) : false;
    }
    else {
        let found = false;
        if (to > from) {
            state.doc.nodesBetween(from, to, (node) => {
                const mark = type.isInSet(node.marks);
                if (mark && some([mark.attrs], attrs))
                    found = true;
                return !found;
            });
        }
        return found;
    }
}
export function bindCommandToKey(command) {
    return (state, dispatch, view) => {
        if (command.enable(state)) {
            command.run(state, dispatch, view);
            return true;
        }
        return false;
    };
}
